import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Send, Trash2, Loader2, X, MessageSquare, Zap, ChevronUp } from "lucide-react";
import { Button } from "./components/ui/button";
import { Textarea } from "./components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STARTER_PROMPTS = [
  "Which video drove the most revenue?",
  "What's my best-performing offer?",
  "Why did my CTR drop last week?",
  "How does this month compare to last?",
];

// FastAPI 402 detail is a structured object — render the friendly message.
function apiErrorMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.message || d.code || fallback;
  return fallback;
}

/**
 * Lightweight markdown renderer for AI responses.
 *
 * The model returns markdown (we ask for it in the system prompt). We don't
 * want to pull in a 200kb markdown lib for this one feature, so we handle
 * just the four shapes the AI actually emits: **bold**, headings, bullets,
 * and `inline code`. Anything else falls through as plain text.
 */
function renderMarkdown(text) {
  const lines = text.split("\n");
  const out = [];
  let bulletGroup = [];

  const flushBullets = () => {
    if (bulletGroup.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-2 text-zinc-200">
          {bulletGroup.map((b, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: renderInline(b) }} />
          ))}
        </ul>
      );
      bulletGroup = [];
    }
  };

  const renderInline = (s) =>
    s
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/`([^`]+)`/g, '<code class="font-mono text-[12px] px-1.5 py-0.5 rounded bg-white/[0.06] text-amber-200">$1</code>');

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (line.startsWith("### ")) {
      flushBullets();
      out.push(<h4 key={idx} className="text-white font-semibold mt-3 mb-1.5 text-sm">{line.slice(4)}</h4>);
    } else if (line.startsWith("## ")) {
      flushBullets();
      out.push(<h3 key={idx} className="text-white font-semibold mt-3 mb-1.5">{line.slice(3)}</h3>);
    } else if (/^\s*[-*]\s+/.test(line)) {
      bulletGroup.push(line.replace(/^\s*[-*]\s+/, ""));
    } else if (line === "") {
      flushBullets();
    } else {
      flushBullets();
      out.push(
        <p key={idx} className="text-zinc-200 leading-relaxed my-1.5" dangerouslySetInnerHTML={{ __html: renderInline(line) }} />
      );
    }
  });
  flushBullets();
  return out;
}

/**
 * Shared chat hook. Used by both the full-page Ask AI tab and the floating
 * Intercom-style bubble so they share state (history, quota, fast-mode
 * toggle) without prop drilling.
 */
function useAiChat({ days, onUpgrade }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [fastMode, setFastMode] = useState(false);
  const [quota, setQuota] = useState({ used: 0, limit: 5, remaining: 5, is_paid: false });
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const [hist, q] = await Promise.all([
        axios.get(`${API}/ai/chat/history`),
        axios.get(`${API}/ai/chat/quota`),
      ]);
      setMessages(hist.data?.messages || []);
      setQuota(q.data);
    } catch (e) {
      // Silent — user may just not be authenticated yet
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const send = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;

    // Optimistic: show the user message immediately
    const optimistic = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    setInput("");
    setSending(true);

    try {
      const { data } = await axios.post(`${API}/ai/chat/message`, {
        message: text,
        days,
        fast_mode: fastMode,
      });
      // Replace optimistic with confirmed user message + assistant response
      setMessages((m) => {
        const without = m.filter((x) => x.id !== optimistic.id);
        return [...without, data.user_message, data.assistant_message];
      });
      // Refresh quota
      try {
        const q = await axios.get(`${API}/ai/chat/quota`);
        setQuota(q.data);
      } catch (_) {}
    } catch (err) {
      // Roll back optimistic on error
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      // 429 usage_cap_hit → global interceptor already pops the upgrade modal,
      // so we skip the toast (would be double-noise) and let the modal drive the CTA.
      const detail = err?.response?.data?.detail;
      const isCapHit = err?.response?.status === 429 && detail && typeof detail === "object" && detail.code === "usage_cap_hit";
      if (!isCapHit) {
        const msg = apiErrorMsg(err, "Couldn't get a response. Try again?");
        toast.error(msg);
      }
      // 402 → bounce to pricing
      if (err?.response?.status === 402 && onUpgrade) {
        setTimeout(() => onUpgrade(), 800);
      }
    } finally {
      setSending(false);
    }
  }, [input, sending, days, fastMode, onUpgrade]);

  const clearAll = useCallback(async () => {
    if (!window.confirm("Clear the entire conversation? Your message quota will NOT reset (it's lifetime, not per-conversation).")) return;
    try {
      await axios.delete(`${API}/ai/chat/history`);
      setMessages([]);
      toast.success("Conversation cleared.");
    } catch (e) {
      toast.error(apiErrorMsg(e, "Couldn't clear conversation."));
    }
  }, []);

  return { messages, input, setInput, sending, fastMode, setFastMode, quota, loadingHistory, send, clearAll, reload: loadHistory };
}

/**
 * Full-page Ask AI tab — mounted when sidebar nav = `ask-ai`.
 */
export function AskAiScreen({ days, onUpgrade }) {
  const ctx = useAiChat({ days, onUpgrade });
  return (
    <div className="max-w-4xl mx-auto" data-testid="ask-ai-screen">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-white">Ask AI</h1>
            <p className="text-sm text-zinc-400">Your data analyst — ask anything about your videos, sales, or offers.</p>
          </div>
        </div>
      </div>

      <ChatBody ctx={ctx} variant="full" />
    </div>
  );
}

/**
 * Floating Intercom-style bubble. Mounted globally in App.js so it follows
 * the user across every screen.
 */
export function AskAiBubble({ days, onUpgrade, currentView }) {
  const [open, setOpen] = useState(false);
  const ctx = useAiChat({ days, onUpgrade });

  // Hide bubble when user is already on the full Ask AI screen (avoids
  // having two chat instances visible at once).
  if (currentView === "ask-ai") return null;

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="ai-bubble-launcher"
          className="fixed bottom-20 right-6 z-50 group flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-gradient-to-br from-amber-500 to-red-500 text-white shadow-[0_8px_32px_-8px_rgba(255,80,0,0.6)] hover:shadow-[0_12px_40px_-8px_rgba(255,80,0,0.85)] hover:scale-[1.03] transition-all"
        >
          <Sparkles className="w-5 h-5" />
          <span className="text-sm font-semibold">Ask AI</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-20 right-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-7rem)] rounded-2xl border border-white/10 bg-zinc-950/95 backdrop-blur-2xl shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden"
          data-testid="ai-bubble-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-amber-500/[0.08] to-red-500/[0.08]">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span className="text-sm font-semibold text-white">Ask AI</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="ai-bubble-close"
              className="text-zinc-400 hover:text-white p-1 rounded-md hover:bg-white/[0.06] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <ChatBody ctx={ctx} variant="bubble" />
        </div>
      )}
    </>
  );
}

/**
 * Shared chat body — message list + input bar. Used by both surfaces.
 * `variant='bubble'` makes it more compact for the floating panel.
 */
function ChatBody({ ctx, variant }) {
  const { messages, input, setInput, sending, fastMode, setFastMode, quota, loadingHistory, send, clearAll } = ctx;
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const isEmpty = messages.length === 0 && !loadingHistory;
  const remainingPill = !quota.is_paid && quota.limit != null;

  const handleSubmit = (e) => {
    e.preventDefault();
    send();
  };

  return (
    <div className={`flex flex-col ${variant === "bubble" ? "flex-1 min-h-0" : "h-[calc(100vh-260px)] min-h-[500px] rounded-2xl border border-white/10 bg-white/[0.02]"}`}>
      {/* Quota / settings strip */}
      <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFastMode(!fastMode)}
            data-testid="ai-fast-mode-toggle"
            className={`flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded-md border transition-colors ${
              fastMode
                ? "bg-amber-500/15 border-amber-500/40 text-amber-200"
                : "bg-white/[0.03] border-white/10 text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Zap className="w-3 h-3" />
            {fastMode ? "Fast mode (Haiku)" : "Smart mode (Sonnet)"}
          </button>
          {remainingPill && (
            <span
              className={`text-[11px] font-medium px-2 py-1 rounded-md border ${
                quota.remaining > 0
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                  : "bg-red-500/10 border-red-500/30 text-red-300"
              }`}
              data-testid="ai-quota-pill"
            >
              {quota.remaining}/{quota.limit} free left
            </span>
          )}
          {quota.is_paid && (
            <span className="text-[11px] font-medium px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300">
              Unlimited
            </span>
          )}
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            data-testid="ai-clear-history"
            className="text-[11px] text-zinc-500 hover:text-red-300 flex items-center gap-1 transition-colors"
          >
            <Trash2 className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Message scroll area */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4"
        data-testid="ai-messages-scroll"
      >
        {loadingHistory ? (
          <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading conversation…
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full text-center" data-testid="ai-empty-state">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/15 to-red-500/15 border border-amber-500/30 flex items-center justify-center mb-4">
              <Sparkles className="w-7 h-7 text-amber-300" />
            </div>
            <h3 className="text-white font-semibold mb-1">Ask anything about your data</h3>
            <p className="text-sm text-zinc-400 mb-5 max-w-xs">I can see your videos, sales, offers, and the time range you've filtered to.</p>
            <div className="grid gap-2 w-full max-w-sm">
              {STARTER_PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => send(p)}
                  data-testid={`ai-starter-${p.slice(0, 20).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  className="text-left text-sm text-zinc-300 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-amber-500/30 rounded-lg px-3 py-2.5 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-zinc-500 text-sm pl-2" data-testid="ai-typing-indicator">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Thinking…</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Input bar */}
      <form onSubmit={handleSubmit} className="border-t border-white/5 p-3 flex gap-2 items-end bg-black/30">
        <Textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={quota.is_paid || quota.remaining > 0 ? "Ask about your videos, sales, offers…" : "Upgrade to unlock more messages"}
          rows={1}
          disabled={sending || (!quota.is_paid && quota.remaining <= 0)}
          className="bg-black/40 border-white/10 text-sm text-white resize-none min-h-[40px] max-h-32 flex-1"
          data-testid="ai-input"
        />
        <Button
          type="submit"
          disabled={sending || !input.trim() || (!quota.is_paid && quota.remaining <= 0)}
          className="bg-gradient-to-br from-amber-500 to-red-500 hover:from-amber-400 hover:to-red-400 text-white h-10 px-3.5 flex-shrink-0"
          data-testid="ai-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`ai-message-${msg.role}`}>
      <div
        className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm ${
          isUser
            ? "bg-gradient-to-br from-amber-500/15 to-red-500/15 border border-amber-500/30 text-white"
            : "bg-white/[0.04] border border-white/10 text-zinc-200"
        }`}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap">{msg.content}</div>
        ) : (
          <div className="prose prose-invert prose-sm max-w-none">{renderMarkdown(msg.content)}</div>
        )}
      </div>
    </div>
  );
}
